import { localizeNumber } from "@devforce/trailhead-client-locale";
import { api, LightningElement, wire } from "lwc";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { createTrack, TrackEvent } from "common/track";

import * as locales from "./locales";

import type { Config } from "./types";
export type { Config };

export default class extends LightningElement {
  @api config: Config = null!;
  // HACK: Allow Storybook to force the layout
  @api __layout: "mobile" | "desktop" | null = null;

  private track = createTrack<"tds-header">(this);

  private isMobile = mediaQueryMobileDefault.matches;

  private _mediaQueryMobile = mediaQueryMobileDefault;

  private get mediaQueryMobile() {
    return this._mediaQueryMobile;
  }

  private set mediaQueryMobile(mq) {
    this._mediaQueryMobile.removeEventListener(
      "change",
      this.onMediaQueryChange
    );
    this._mediaQueryMobile = mq;
    this._mediaQueryMobile.addEventListener("change", this.onMediaQueryChange);
  }

  @wire(LabelsAdapter, { locales, options: "$labelsOptions" })
  private _labels: LabelsAdapterValue = {};
  private get labels(): LabelsAdapterValue {
    return {
      ...this._labels,
      userStats: this.userHasStats ? this._labels.userStats : "",
    };
  }

  private get labelsOptions() {
    let { config, userHasStats } = this;
    const points =
      userHasStats && config.userProfile?.pointsCount
        ? localizeNumber(config.userProfile.pointsCount)
        : 0;
    const badges = userHasStats ? config.userProfile!.badgesCount! : 0;

    return {
      userStats: {
        badges,
        points,
      },
      userStatsWithRank: {
        badges,
        points,
        currentRank: userHasStats ? config.userProfile!.currentRank! : "",
      },
    };
  }

  private get logo() {
    return this.isMobile
      ? this.config.logoMobile || this.config.logo
      : this.config.logo;
  }

  private get skipLink() {
    return this.config.skipLink ?? { href: "#main" };
  }

  private get style() {
    let styles: string[] = [];
    if (this.config.theme) {
      styles.push(
        `--tds-header_theme-color-default: ${this.config.theme.primary}`,
        `--tds-header_theme-color-dark-default: ${this.config.theme.primaryDark}`,
        `--tds-header_theme-color-light-default: ${this.config.theme.primaryLight}`
      );
    }
    return styles.join(";");
  }

  private get userHasStats() {
    return (
      this.config &&
      this.config.userProfile &&
      typeof this.config.userProfile.badgesCount === "number" &&
      typeof this.config.userProfile.pointsCount === "number"
    );
  }

  connectedCallback() {
    if (this.__layout) {
      this.isMobile = this.__layout === "mobile";
    } else {
      this.mediaQueryMobile.addEventListener("change", this.onMediaQueryChange);
    }
  }

  disconnectedCallback() {
    this.mediaQueryMobile.removeEventListener(
      "change",
      this.onMediaQueryChange
    );
  }

  private handleTrack(e: TrackEvent<"tds-header">) {
    let eventName = e.detail.eventName;
    this.track(eventName, e.detail.payload);
  }

  private onDesktopNavOverflow(
    e: CustomEvent<{ overflow: boolean; minWidth: number }>
  ) {
    // If the nav overflows, adjust the mobile media query to be
    // the minimum amount of spaces needed to display the nav without overflow
    if (e.detail.overflow) {
      this.isMobile = true;
      this.mediaQueryMobile = window.matchMedia(
        `(max-width: ${e.detail.minWidth}px)`
      );
    }
  }

  private onMediaQueryChange = (e: MediaQueryListEvent) => {
    this.isMobile = e.matches;
  };
}

export const mediaQueryMobileDefault = window.matchMedia("(max-width: 760px)");
